import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { LayoutDashboard, Pencil, ShieldCheck, X } from "lucide-react";

const DASHBOARDS = [
  { key: "main",   label: "Main Dashboard",       accessFlag: "has_main",   sectionsField: "sections",        defsField: "sections" },
  { key: "debtor", label: "Debtor/HR Dashboard",  accessFlag: "has_debtor", sectionsField: "debtor_sections", defsField: "debtor_sections" },
  { key: "ops",    label: "Operations Dashboard", accessFlag: "has_ops",    sectionsField: "ops_sections",    defsField: "ops_sections" },
];

export function DashboardAccessPanel() {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null); // {user, dash}
  const [picked, setPicked] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/dashboard-access")
    .then((r) => setData(r.data))
    .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load"));
  useEffect(() => { load(); }, []);

  if (!data) return <div className="py-10 text-sm text-slate-500">Loading…</div>;

  const defsFor = (dash) => data[dash.defsField] || [];

  function openEdit(u, dash) {
    const cur = u[dash.sectionsField];
    setEditing({ user: u, dash });
    setPicked(cur === null || cur === undefined ? defsFor(dash).map((s) => s.key) : cur);
  }

  async function save() {
    const { user: u, dash } = editing;
    const defs = defsFor(dash);
    const all = picked.length === defs.length;
    setSaving(true);
    try {
      await api.put(`/admin/dashboard-access/${u.id}`,
        { sections: all ? null : picked, dashboard: dash.key });
      toast.success(`${u.name || u.email} · ${dash.label}: ${all ? "all sections" : `${picked.length} of ${defs.length} sections`}`);
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    setSaving(false);
  }

  function cell(u, dash) {
    if (!u[dash.accessFlag]) return <span className="text-slate-300">— no access</span>;
    if (u.is_admin) return (
      <span className="inline-flex items-center gap-1 text-emerald-700 font-bold"><ShieldCheck size={12} /> Full (admin)</span>
    );
    const cur = u[dash.sectionsField];
    const total = defsFor(dash).length;
    return (
      <span className="inline-flex items-center gap-2">
        {cur === null || cur === undefined ? (
          <span className="text-slate-600 font-bold">All sections</span>
        ) : (
          <span className="text-amber-700 font-bold" title={(cur || []).join(", ")}>
            {cur.length} of {total}
          </span>
        )}
        <button data-testid={`dash-access-edit-${dash.key}-${u.email}`} onClick={() => openEdit(u, dash)}
          className="border border-slate-300 text-slate-600 px-2 py-1 text-[10px] uppercase tracking-wider font-bold hover:border-[#F97316] hover:text-[#F97316] inline-flex items-center gap-1">
          <Pencil size={10} /> Edit
        </button>
      </span>
    );
  }

  return (
    <div data-testid="dashboard-access-panel">
      <div className="mb-3 text-xs text-slate-500 flex items-center gap-2">
        <LayoutDashboard size={13} className="text-[#F97316]" />
        Restrict which sections each user sees on the Main, Debtor/HR and Operations dashboards — hidden sections are stripped on the server, not just hidden.
      </div>
      <div className="bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full" data-testid="dashboard-access-table">
          <thead className="bg-slate-50 border-b-2 border-slate-200">
            <tr>{["Name", "Email", "Role", ...DASHBOARDS.map((d) => d.label)].map((h) => (
              <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-3 px-5">{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {data.users.map((u) => (
              <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`dash-access-row-${u.email}`}>
                <td className="py-2.5 px-5 text-sm font-bold text-slate-800">{u.name || "—"}</td>
                <td className="py-2.5 px-5 text-xs text-slate-500">{u.email}</td>
                <td className="py-2.5 px-5 text-xs uppercase font-bold text-slate-500">{u.role}</td>
                {DASHBOARDS.map((d) => (
                  <td key={d.key} className="py-2.5 px-5 text-xs whitespace-nowrap">{cell(u, d)}</td>
                ))}
              </tr>
            ))}
            {!data.users.length && <tr><td colSpan={3 + DASHBOARDS.length} className="py-6 text-center text-sm text-slate-400">No users with dashboard access.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white w-full max-w-lg max-h-[85vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="dash-access-modal">
            <div className="px-5 py-3.5 bg-[#0B1F3A] text-white flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300 font-bold">{editing.dash.label} — Sections</div>
                <div className="font-bold text-sm">{editing.user.name || editing.user.email}</div>
              </div>
              <button onClick={() => setEditing(null)} data-testid="dash-access-close"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-1">
              <label className="flex items-center gap-2 pb-2 mb-2 border-b border-slate-200 cursor-pointer select-none">
                <input type="checkbox" data-testid="dash-access-all"
                  checked={picked.length === defsFor(editing.dash).length}
                  onChange={(e) => setPicked(e.target.checked ? defsFor(editing.dash).map((s) => s.key) : [])} />
                <span className="text-xs font-black uppercase tracking-wider text-slate-700">All sections (no restriction)</span>
              </label>
              {defsFor(editing.dash).map((s) => (
                <label key={s.key} className="flex items-center gap-2 py-1 cursor-pointer select-none hover:bg-slate-50 px-1">
                  <input type="checkbox" data-testid={`dash-access-sec-${s.key}`}
                    checked={picked.includes(s.key)}
                    onChange={(e) => setPicked(e.target.checked ? [...picked, s.key] : picked.filter((k) => k !== s.key))} />
                  <span className="text-xs text-slate-700">{s.label}</span>
                </label>
              ))}
            </div>
            <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="px-4 py-2 text-xs uppercase tracking-wider font-bold text-slate-500">Cancel</button>
              <button data-testid="dash-access-save" onClick={save} disabled={saving}
                className="bg-[#F97316] hover:bg-[#EA580C] text-white px-4 py-2 text-xs uppercase tracking-wider font-bold disabled:opacity-50">
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
